import GridView from "components/GridView";
import { GridSchema, GridViewHeaderProps } from "components/GridView/GridView.types";
import { prepareFilters } from "components/GridView/components/GridFilter";
import useGridStore from "components/GridView/grid.store";
import { useBreadcrumb } from "contexts/breadcrumb/BreadcrumbContext";
import { useCookieContext } from "contexts";
import useFileDownload from "hooks/useFileDownload";
import { useNotification } from "hooks/useNotification";
import { useTitle } from "hooks/useTitle";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Select from "react-select";
import { OrdersService } from "services/orders";
import {
  OrderByProgramIdResult,
  OrdersByProgramColumnDefinition,
} from "services/orders/models/OrdersTypes";
import { ProgramsService } from "services/programs";
import { useShallow } from "zustand/react/shallow";

const PAGE_SIZE = 10;

const formatDynamicValue = (value: any): string => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "boolean") {
    return value ? "True" : "False";
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "-";
    }
  }

  return String(value);
};

const ProgramsReportView = () => {
  const { setBreadcrumbs } = useBreadcrumb();
  const { t, i18n } = useTranslation(["Reports", "Orders", "Common"]);
  const { isAr } = useCookieContext();
  const { showNotification } = useNotification();
  const { toSystemThemeDateFormat } = useSystemDateTheme();
  const downloadFile = useFileDownload();

  const [loading, setLoading] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
  const [programs, setPrograms] = useState<{ id: number; name: string }[]>([]);
  const [rows, setRows] = useState<OrderByProgramIdResult[]>([]);
  const [columns, setColumns] = useState<OrdersByProgramColumnDefinition[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const programOptions = useMemo(
    () => programs.map((program) => ({ value: program.id, label: program.name })),
    [programs],
  );

  const ready = useRef(false);

  const { sortState, pagination, setPagination, searchValue, filters, resetGridStore } = useGridStore(
    useShallow((state) => ({
      sortState: state.sortState,
      pagination: state.pagination,
      setPagination: state.setPagination,
      searchValue: state.generalSearch,
      filters: state.filters,
      resetGridStore: state.reset,
    })),
  );

  useTitle(t("Reports:programsReport"));

  const loadPrograms = useCallback(async () => {
    try {
      setLoadingPrograms(true);
      const response = await ProgramsService.getAllProgramsAsync({
        pageIndex: 1,
        pageSize: 1000,
      });

      const mappedPrograms = (response.data.result ?? []).map((program) => ({
        id: program.id,
        name: program.programDetail?.displayName ?? `#${program.id}`,
      }));

      setPrograms(mappedPrograms);
    } catch {
      showNotification({
        type: "error",
        description: t("Common:internalServerError"),
      });
    } finally {
      setLoadingPrograms(false);
    }
  }, [showNotification, t]);

  const fetchOrdersByProgramId = useCallback(async ({
    resetPagination = false,
    pageIndex,
  }: {
    resetPagination?: boolean;
    pageIndex?: number;
  } = {}) => {
    if (!selectedProgramId) {
      setRows([]);
      setColumns([]);
      setTotalCount(0);
      return;
    }

    try {
      setLoading(true);
      const response = await OrdersService.getAllOrdersByProgramId(selectedProgramId, {
        pageIndex: resetPagination ? 1 : (pageIndex ?? 1),
        pageSize: PAGE_SIZE,
        search: searchValue,
        sort: Object.keys(sortState ?? {}).map((key) => ({
          field: key,
          dir: sortState[key],
        })),
        filter: prepareFilters(filters),
      });

      const data = response.data;
      setRows(data.orders?.result ?? []);
      setColumns(data.columns ?? []);
      setTotalCount(data.orders?.totalCount ?? 0);

      if (resetPagination) {
        setPagination({
          currentPage: 1,
          totalPages: Math.ceil((data.orders?.totalCount ?? 0) / PAGE_SIZE),
        });
      }
    } catch {
      showNotification({
        type: "error",
        description: t("Common:internalServerError"),
      });
      setRows([]);
      setColumns([]);
      setTotalCount(0);
      setPagination({ currentPage: 1, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  }, [filters, searchValue, selectedProgramId, setPagination, showNotification, sortState, t]);

  useEffect(() => {
    setBreadcrumbs([
      {
        localizationKey: "reports",
        path: "/home/reports",
        menuItemId: "reports",
      },
      {
        localizationKey: "programsReport",
        path: "/home/reports/programsReport",
        menuItemId: "programsReport",
      },
    ]);

    loadPrograms();
  }, [i18n.language, loadPrograms, setBreadcrumbs]);

  useEffect(() => {
    if (!ready.current) {
      resetGridStore();
      ready.current = true;
      return;
    }
  }, [resetGridStore]);

  useEffect(() => {
    if (!ready.current || !selectedProgramId) {
      return;
    }

    fetchOrdersByProgramId({ resetPagination: true });
  }, [selectedProgramId, sortState, searchValue, filters, isAr, fetchOrdersByProgramId]);

  useEffect(() => {
    if (!ready.current || !selectedProgramId) {
      return;
    }

    fetchOrdersByProgramId({ pageIndex: pagination.currentPage });
  }, [fetchOrdersByProgramId, pagination.currentPage, selectedProgramId]);

  const gridSchema = useMemo<GridSchema[]>(() => {
    const fixedColumns: GridSchema[] = [
      {
        accessor: "serialNumber",
        field: "SerialNumber",
        displayName: t("Orders:orderNumber"),
        sortable: true,
        showOnMobile: true,
        render: (row: OrderByProgramIdResult) => {
          return <Link to={`/home/orders/view/${row.id}`}>{row.serialNumber ?? row.id}</Link>;
        },
      },
      {
        accessor: "programName",
        field: "ProgramName",
        displayName: t("Orders:programName"),
        showOnMobile: true,
      },
      {
        accessor: "appliedUserName",
        field: "AppliedUserName",
        displayName: t("Orders:applicantName"),
        showOnMobile: true,
      },
      {
        accessor: "finalCommitteeDecision",
        field: "FinalCommitteeDecision",
        displayName: t("Orders:researcherClassification"),
        render: (row: OrderByProgramIdResult) => {
          return <>{t(`Orders:${row.finalCommitteeDecision ?? "-"}`)}</>;
        },
      },
      {
        accessor: "createdOn",
        field: "CreatedOn",
        displayName: t("Orders:submissionDate"),
        sortable: true,
        render: (row: OrderByProgramIdResult) => {
          return <>{row.createdOn ? toSystemThemeDateFormat(row.createdOn) : "-"}</>;
        },
      },
      {
        accessor: "centreManagementName",
        field: "CentreManagementName",
        displayName: t("Orders:researchesCenter"),
      },
      {
        accessor: "orderPhase",
        field: "OrderPhase",
        displayName: t("Orders:orderStage"),
      },
      {
        accessor: "centreManagerName",
        field: "CentreManagerName",
        displayName: t("Orders:responsibleParty"),
      },
      {
        accessor: "orderStatus",
        field: "OrderStatus",
        displayName: t("Orders:orderStatus"),
        render: (row: OrderByProgramIdResult) => {
          const statusClassName = row.orderStatus === "Completed" ? "bg-success text-success" : "bg-primary text-primary";
          return <span className={`badge bg-opacity-10 rounded-4 ${statusClassName} py-2`}>{t(row.orderStatus ?? "-")}</span>;
        },
      },
      {
        accessor: "formSubject",
        field: "FormSubject",
        displayName: t("Orders:formSubject"),
        sortable: true,
        showOnMobile: true,
      },
    ];

    const dynamicColumns: GridSchema[] = columns.map((column) => ({
      field: `formField_${column.key}`,
      displayName: isAr ? column.labelAr || column.labelEn : column.labelEn || column.labelAr,
      render: (row: OrderByProgramIdResult) => formatDynamicValue(row.formFields?.[column.key]),
    }));

    return [...fixedColumns, ...dynamicColumns];
  }, [columns, isAr, t, toSystemThemeDateFormat]);

  const headerActions = (
    <div className="d-flex align-items-center gap-2">
      <span className="text-nowrap">{t("Reports:program")}</span>
      <div style={{ minWidth: "260px" }}>
        <Select
          isClearable
          isLoading={loadingPrograms}
          isRtl={isAr}
          placeholder={t("Reports:selectProgram")}
          options={programOptions}
          value={programOptions.find((option) => option.value === selectedProgramId) ?? null}
          onChange={(selectedOption) => setSelectedProgramId(selectedOption?.value ?? null)}
        />
      </div>
    </div>
  );

  const viewHeader: GridViewHeaderProps = {
    title: t("Reports:programsReport"),
    singularName: t("Orders:order"),
    totalCount,
    hideAddButton: true,
    hideFilterButton: true,
    actionsComponent: headerActions,
    onExportClick: async () => {
      if (!selectedProgramId) {
        showNotification({
          type: "warning",
          description: t("Reports:selectProgramFirst"),
        });
        return;
      }

      try {
        setLoading(true);
        const response = await OrdersService.exportOrdersByProgramIdToExcel(selectedProgramId, {
          pageIndex: pagination.currentPage,
          pageSize: PAGE_SIZE,
          search: searchValue,
          sort: Object.keys(sortState ?? {}).map((key) => ({
            field: key,
            dir: sortState[key],
          })),
          filter: prepareFilters(filters),
        });
        await downloadFile(response, "programs-report.xlsx");
      } catch {
        showNotification({
          type: "error",
          description: t("Common:internalServerError"),
        });
      } finally {
        setLoading(false);
      }
    },
  };

  return (
    <div className="bg-white rounded-4 h-100">
      <GridView
        loading={loading}
        viewHeaderProps={viewHeader}
        gridProps={{
          data: rows,
          gridSchema,
        }}
      />
    </div>
  );
};

export default ProgramsReportView;


